import React, { useEffect, useState } from 'react'
import './OurBlogs.css'
import { useGetBlogDataQuery, useGetCategoriesQuery } from '../../../Api/blogApi'
import OwlCarousel from 'react-owl-carousel2';
import "react-owl-carousel2/lib/styles.css";
import TagManager from "react-gtm-module";
import Aos from "aos";
import "aos/dist/aos.css";
import { useNavigate } from 'react-router-dom';

const OurBlogs = () => {
  const carouselRef = React.useRef(null);
  const navigate = useNavigate();

  const { data: blogData, isLoading, isError } = useGetBlogDataQuery()
  const [blogs, setBlogs] = useState([])

  const { data: categoriesData, isLoading: isCategoriesLoading, isError: isCategoriesError } = useGetCategoriesQuery();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const blogs = Array.isArray(blogData) && blogData.length > 0 
      ? blogData
      : [];
    setBlogs(blogs);
  }, [blogData])

  useEffect(() => {
    if (categoriesData && Array.isArray(categoriesData)) {
      setCategories(categoriesData);
    }
  }, [categoriesData]);

  // Responsive carousel options
  const getCarouselOptions = () => {
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth <= 992;
    
    return {
      nav: false, 
      dots: true,
      loop: true,
      autoplay: false,
      buttons: false,
      startPosition: 0,
      duration: 200,
      responsive: {
        0: {
          items: 1,
          margin: 16
        },
        576: {
          items: 1,
          margin: 20
        },
        768: {
          items: 2,
          margin: 24
        },
        992: {
          items: 2,
          margin: 24
        },
        1200: {
          items: 3,
          margin: 24
        }
      },
      // Mobile-specific settings
      ...(isMobile && {
        touchDrag: true,
        mouseDrag: false,
        pullDrag: true,
        freeDrag: false
      }),
      // Tablet and desktop settings
      ...(!isMobile && {
        touchDrag: true,
        mouseDrag: true,
        pullDrag: true,
        freeDrag: false
      })
    };
  };

  const handleBlogClick = (blogId) => {
    navigate(`/blogs/${blogId}`);
  };

  const handleShareClick = (e, blog) => {
    e.stopPropagation(); // Prevent navigation when clicking share button
    
    if (navigator.share) {
      navigator.share({
        title: blog.title.rendered,
        url: window.location.origin + `/blogs/${blog.id}`,
      });
    } else {
      // Fallback: copy to clipboard
      const url = window.location.origin + `/blogs/${blog.id}`;
      navigator.clipboard.writeText(url).then(() => {
        alert('Blog URL copied to clipboard!');
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date available';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <section className="our-blogs-section" aria-labelledby="blogs-section-title">
      <div className="container">
        <header className="section-header">
          <div>
            <h2 id="blogs-section-title" className="section-title">Our Blogs</h2>
            <p className="section-subtitle">Insights, Innovations & Trends – Explore the Future of Tech!</p>
          </div>
          <button 
            className="view-all-btn" 
            onClick={() => navigate('/blogs')}
            aria-label="View all blogs"
          >
            View All Blogs
          </button>
        </header>
      </div>
     
      {isLoading && (
        <div className="text-center my-4" role="status" aria-live="polite">
          <span>Loading blogs...</span>
        </div>
      )}
      
      {isError && (
        <div className="alert alert-danger my-4" role="alert">
          Failed to load blogs. Please try again later.
        </div>
      )}
      
      {!isLoading && !isError && blogs.length === 0 && (
        <div className="text-center my-4">
          <span>No blogs available at the moment.</span>
        </div>
      )}
      
      {!isLoading && !isError && blogs.length > 0 && (
        <div className="blogs-slider-outer container">
          <OwlCarousel options={getCarouselOptions()} ref={carouselRef}>
            {blogs.map((blog) => (
              <article 
                key={blog.id} 
                className="blog-card-outer" 
                onClick={() => handleBlogClick(blog.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBlogClick(blog.id);
                  }
                }}
                aria-label={`Read blog: ${blog.title.rendered}`}
              >
                <div className="blog-image">
                  <img 
                    src={blog?.featured_image || require('../../../assets/images/blogdummy.jpeg')} 
                    alt={`Featured image for ${blog.title.rendered}`}
                    loading="lazy"
                  />
                </div>
                <div className="blog-content">
                  <div className="blog-tags" role="list">
                    {blog.categories && blog.categories.map((category, index) => {
                      const categoryName = categories.find(c => c.id === category)?.name;
                      return categoryName ? (
                        <span key={index} className="blog-tag-pill" role="listitem">
                          {categoryName}
                        </span>
                      ) : null;
                    })}
                  </div>
                  <h3 className="blog-title-2lines">{blog.title.rendered}</h3>
                  <div className="blog-meta-row">
                    <div className="blog-meta-row-left">
                      <span className="views" aria-label={`${blog.view_count} views`}>
                        <i className="far fa-eye" aria-hidden="true"></i>
                        {blog.view_count} Views
                      </span>
                      <span className="date" aria-label={`Published on ${formatDate(blog.date)}`}>
                        <i className="far fa-calendar" aria-hidden="true"></i>
                        {formatDate(blog.date)}
                      </span>
                    </div>
                    <button 
                      className="btn btn-outline-primary share-btn"
                      onClick={(e) => handleShareClick(e, blog)}
                      aria-label={`Share ${blog.title.rendered}`}
                    >
                      Share With Others
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </OwlCarousel>
        </div>
      )}
    </section>
  )
}

export default OurBlogs;